/**
 * Multi-Select Component System
 * Handles continent chips, country selector, and generic multi-select functionality
 * Reusable across search filters and settings pages
 */

document.addEventListener('DOMContentLoaded', function() {
    initializeMultiSelectComponents();
});

function initializeMultiSelectComponents() {
    initializeContinentChips();
    initializeCountrySelectors();
    initializeGenericMultiSelects();
}

/**
 * Continent Chips - Checkbox-based continent selection
 */
function initializeContinentChips() {
    const containers = document.querySelectorAll('.continent-chips-container');
    
    containers.forEach(container => {
        const chips = container.querySelectorAll('.filter-checkbox-chip');
        
        chips.forEach(chip => {
            const checkbox = chip.querySelector('input[type="checkbox"]');
            const icon = chip.querySelector('i');
            
            if (!checkbox || !icon) return;
            
            // Handle click events on the label/chip
            chip.addEventListener('click', function(e) {
                // The checkbox state will be toggled automatically by the label click
                // We just need to update the visual appearance after the state change
                setTimeout(() => {
                    updateContinentChipAppearance(chip, checkbox, icon);
                }, 0);
            });
            
            // Initialize appearance based on current checkbox state
            updateContinentChipAppearance(chip, checkbox, icon);
        });
    });
}

function updateContinentChipAppearance(chip, checkbox, icon) {
    // Remove all existing state classes
    chip.classList.remove('bg-white', 'border-gray-300', 'bg-sky-100', 'border-sky-300', 'text-sky-700');
    
    // Remove all color classes from icon
    icon.classList.remove('text-sky-500', 'text-sky-400', 'text-orange-400', 'text-rose-400', 'text-emerald-400', 'text-fuchsia-400', 'text-amber-400', 'text-gray-400');
    
    if (checkbox.checked) {
        // Selected state
        chip.classList.add('bg-sky-100', 'border-sky-300', 'text-sky-700');
        icon.classList.add('text-sky-500');
    } else {
        // Unselected state
        chip.classList.add('bg-white', 'border-gray-300');
        
        // Restore original color based on continent
        if (icon.classList.contains('fa-earth-europe')) {
            icon.classList.add('text-sky-400');
        } else if (icon.classList.contains('fa-earth-africa')) {
            icon.classList.add('text-orange-400');
        } else if (icon.classList.contains('fa-earth-asia')) {
            icon.classList.add('text-rose-400');
        } else if (icon.classList.contains('fa-earth-americas')) {
            const isNorthAmerica = chip.textContent.includes('North');
            icon.classList.add(isNorthAmerica ? 'text-emerald-400' : 'text-fuchsia-400');
        } else if (icon.classList.contains('fa-earth-oceania')) {
            icon.classList.add('text-amber-400');
        } else {
            icon.classList.add('text-gray-400');
        }
    }
}

/**
 * Country Selector - Searchable country dropdown with chips
 */
function initializeCountrySelectors() {
    const selectors = document.querySelectorAll('.country-selector-container');
    
    selectors.forEach(selector => {
        const searchInput = selector.querySelector('.country-search');
        const dropdown = selector.querySelector('.country-options');
        const chipsContainer = selector.querySelector('.selected-countries-chips');
        
        if (!searchInput || !dropdown || !chipsContainer) return;
        
        // Show dropdown on focus
        searchInput.addEventListener('focus', () => {
            dropdown.style.display = 'block';
            filterCountryOptions(selector, searchInput.value);
        });
        
        // Hide dropdown on blur (with delay to allow clicks)
        searchInput.addEventListener('blur', (e) => {
            setTimeout(() => {
                dropdown.style.display = 'none';
            }, 150);
        });
        
        // Filter options as user types
        searchInput.addEventListener('input', (e) => {
            // Make sure dropdown is visible when typing
            if (dropdown.style.display !== 'block') {
                dropdown.style.display = 'block';
            }
            filterCountryOptions(selector, e.target.value);
        });
        
        // Handle country selection
        dropdown.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const option = e.target.closest('.country-option');
            if (option) {
                const code = option.dataset.countryCode;
                const name = option.dataset.countryName;
                addCountryChip(selector, code, name);
                searchInput.value = '';
                
                // Reset search filtering to show all available countries
                filterCountryOptions(selector, '');
                
                // Keep dropdown open for multiple selections
                dropdown.style.display = 'block';
            }
        });
        
        // Handle chip removal
        chipsContainer.addEventListener('click', (e) => {
            const removeBtn = e.target.closest('.remove-country');
            if (removeBtn) {
                const code = removeBtn.dataset.countryCode;
                removeCountryChip(selector, code);
            }
        });
        
        // Hide dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!selector.contains(e.target)) {
                dropdown.style.display = 'none';
            }
        });
    });
}

function filterCountryOptions(selector, searchTerm) {
    const options = selector.querySelectorAll('.country-option');
    const term = searchTerm.toLowerCase();
    
    let visibleCount = 0;
    options.forEach(option => {
        const name = option.dataset.countryName.toLowerCase();
        const isAlreadySelected = hasSelectedChip(selector, option.dataset.countryCode);
        
        // Skip already selected countries (they should stay hidden)
        if (isAlreadySelected) {
            option.style.display = 'none';
            option.classList.add('hidden');
            return;
        }
        
        // Show if matches search term
        if (name.includes(term) || term === '') {
            option.style.display = 'flex';
            option.classList.remove('hidden');
            visibleCount++;
        } else {
            option.style.display = 'none';
            option.classList.add('hidden');
        }
    });
    

}

function hasSelectedChip(selector, countryCode) {
    const chipsContainer = selector.querySelector('.selected-countries-chips');
    return chipsContainer.querySelector(`[data-country-code="${countryCode}"]`) !== null;
}

function addCountryChip(selector, code, name) {
    const chipsContainer = selector.querySelector('.selected-countries-chips');
    const dropdown = selector.querySelector('.country-options');
    
    // Get the correct input name from the container ID or existing chips
    const containerId = selector.id;
    let inputName = 'blocked_countries[]'; // default
    if (containerId && containerId.includes('blocked-countries')) {
        inputName = 'blocked_countries[]';
    }
    
    // Create chip element (using the same structure as the template)
    const chip = document.createElement('div');
    chip.className = 'selected-country-chip country-tag flex items-center gap-1 rounded-full px-3 py-1.5 text-sm bg-sky-100 border border-sky-300 text-sky-700';
    chip.setAttribute('data-country', code);
    
    // Use the same flag URL helper as the template  
    const flagHtml = code.length === 2 ? 
        `<span class="country-flag mr-1 inline-block">
            <img src="/images/circle-flags/flags/${code.toLowerCase()}.svg" alt="${code}" class="w-5 h-5 rounded-full">
        </span>` : '';
    
    chip.innerHTML = `
        ${flagHtml}
        <span>${name}</span>
        <button type="button" class="remove-country text-sky-500 hover:text-sky-700 ml-1 border-0 bg-transparent p-0 focus:outline-none" data-country-code="${code}">
            <i class="fas fa-times"></i>
        </button>
        <input type="hidden" name="${inputName}" value="${code}">
    `;
    
    chipsContainer.appendChild(chip);
    
    // Hide the option from dropdown
    const option = dropdown.querySelector(`[data-country-code="${code}"]`);
    if (option) {
        option.style.display = 'none';
    }
    
    // Dispatch custom event for other components to listen to
    document.dispatchEvent(new CustomEvent('countryAdded', {
        detail: { code: code, name: name }
    }));
}

function removeCountryChip(selector, code) {
    const dropdown = selector.querySelector('.country-options');
    
    // Find and remove the chip - look specifically in the chips container, not the dropdown
    const chipsContainer = selector.querySelector('.selected-countries-chips');
    const removeButton = chipsContainer.querySelector(`[data-country-code="${code}"]`);
    if (removeButton) {
        const chip = removeButton.closest('.selected-country-chip');
        if (chip) {
            chip.remove();
        }
    }
    
    // Show option in dropdown again
    const option = dropdown.querySelector(`[data-country-code="${code}"]`);
    if (option) {
        option.style.display = 'flex';
        option.classList.remove('hidden');
    }
    
    // Keep dropdown open and update search results
    dropdown.style.display = 'block';
    const searchInput = selector.querySelector('.country-search');
    if (searchInput) {
        filterCountryOptions(selector, searchInput.value);
    }
    
    // Dispatch custom event for other components to listen to
    document.dispatchEvent(new CustomEvent('countryRemoved', {
        detail: { code: code }
    }));
}

/**
 * Generic Multi-Select - Reusable for any items
 */
function initializeGenericMultiSelects() {
    const selectors = document.querySelectorAll('.generic-multi-select-container');
    
    selectors.forEach(selector => {
        const searchInput = selector.querySelector('.generic-search');
        const dropdown = selector.querySelector('.generic-options');
        const chipsContainer = selector.querySelector('.selected-items-chips');
        
        if (!searchInput || !dropdown || !chipsContainer) return;
        
        // Show dropdown on focus
        searchInput.addEventListener('focus', () => {
            dropdown.style.display = 'block';
            filterGenericOptions(selector, '');
        });
        
        // Filter options as user types
        searchInput.addEventListener('input', (e) => {
            filterGenericOptions(selector, e.target.value);
        });
        
        // Handle item selection
        dropdown.addEventListener('click', (e) => {
            const option = e.target.closest('.option-item');
            if (option) {
                const code = option.dataset.optionCode;
                const name = option.dataset.optionName;
                addGenericChip(selector, code, name);
                searchInput.value = '';
                dropdown.style.display = 'none';
            }
        });
        
        // Handle chip removal
        chipsContainer.addEventListener('click', (e) => {
            const removeBtn = e.target.closest('.remove-item');
            if (removeBtn) {
                const code = removeBtn.dataset.optionCode;
                removeGenericChip(selector, code);
            }
        });
        
        // Hide dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!selector.contains(e.target)) {
                dropdown.style.display = 'none';
            }
        });
    });
}

function filterGenericOptions(selector, searchTerm) {
    const options = selector.querySelectorAll('.option-item');
    const term = searchTerm.toLowerCase();
    
    options.forEach(option => {
        const name = option.dataset.optionName.toLowerCase();
        const isVisible = option.style.display !== 'none';
        
        if (isVisible && (name.includes(term) || term === '')) {
            option.style.display = 'flex';
        } else {
            option.style.display = 'none';
        }
    });
}

function addGenericChip(selector, code, name) {
    const chipsContainer = selector.querySelector('.selected-items-chips');
    const dropdown = selector.querySelector('.generic-options');
    const nameInput = selector.closest('form').querySelector(`input[name*="[]"]`);
    const inputName = nameInput ? nameInput.name : 'items[]';
    const showFlags = selector.querySelector('.option-icon img') !== null;
    
    // Create chip element
    const chip = document.createElement('div');
    chip.className = 'selected-item-chip flex items-center gap-1 rounded-full px-3 py-1.5 text-sm bg-sky-100 border border-sky-300 text-sky-700';
    chip.innerHTML = `
        ${showFlags && code.length === 2 ? `
            <span class="option-icon mr-1 inline-block">
                <img src="/images/flags/${code.toLowerCase()}.png" alt="${code}" class="w-5 h-5 rounded-full">
            </span>
        ` : ''}
        <span>${name}</span>
        <button type="button" class="remove-item text-sky-500 hover:text-sky-700 ml-1 border-0 bg-transparent p-0 focus:outline-none" data-option-code="${code}">
            <i class="fas fa-times"></i>
        </button>
        <input type="hidden" name="${inputName}" value="${code}">
    `;
    
    chipsContainer.appendChild(chip);
    
    // Hide the option from dropdown
    const option = dropdown.querySelector(`[data-option-code="${code}"]`);
    if (option) {
        option.style.display = 'none';
    }
}

function removeGenericChip(selector, code) {
    const chip = selector.querySelector(`[data-option-code="${code}"]`).closest('.selected-item-chip');
    const dropdown = selector.querySelector('.generic-options');
    
    // Remove chip
    if (chip) {
        chip.remove();
    }
    
    // Show option in dropdown again
    const option = dropdown.querySelector(`[data-option-code="${code}"]`);
    if (option) {
        option.style.display = 'flex';
    }
}

// Export functions for potential external use
window.InterPals = window.InterPals || {};
window.InterPals.multiSelect = {
    initializeMultiSelectComponents,
    initializeContinentChips,
    initializeCountrySelectors,
    initializeGenericMultiSelects,
    addCountryChip,
    removeCountryChip,
    addGenericChip,
    removeGenericChip
}; 